import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, GripVertical, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { KgValue } from '@/components/efs/KgValue';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { PackageTypesFormDialog } from './PackageTypesFormDialog';
import { PackageTypesRowActions } from './PackageTypesRowActions';
import { usePackageTypesList, useReorderPackageTypes } from './usePackageTypes';
import type { PackageType } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const PackageTypesPage = () => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const query = usePackageTypesList({ page, per_page: perPage });
  const rows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);
  const total = query.data?.meta?.total ?? 0;
  const { mutate: reorderPackageTypes, isPending: isReordering } = useReorderPackageTypes();

  const canManage = hasPermission(user, 'master.approve');
  const canReorderCurrentPage = canManage && page === 1;

  const changePerPage = useCallback((nextPerPage: number) => {
    setPerPage(nextPerPage);
    setPage(1);
  }, []);

  const reorderPackageTypeRows = useCallback(
    (nextRows: PackageType[]) => {
      reorderPackageTypes(nextRows.map((row) => row.package_type_id));
    },
    [reorderPackageTypes],
  );

  const movePackageType = useCallback(
    (id: string, direction: -1 | 1) => {
      const index = rows.findIndex((row) => row.package_type_id === id);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= rows.length) return;
      const nextRows = [...rows];
      [nextRows[index], nextRows[nextIndex]] = [nextRows[nextIndex], nextRows[index]];
      reorderPackageTypeRows(nextRows);
    },
    [reorderPackageTypeRows, rows],
  );

  const dropPackageType = (targetId: string) => {
    if (!draggingId || draggingId === targetId) return;
    const from = rows.findIndex((row) => row.package_type_id === draggingId);
    const to = rows.findIndex((row) => row.package_type_id === targetId);
    if (from < 0 || to < 0) return;
    const nextRows = [...rows];
    const [moved] = nextRows.splice(from, 1);
    nextRows.splice(to, 0, moved);
    reorderPackageTypeRows(nextRows);
  };

  const columns = useMemo<MasterTableColumn<PackageType>[]>(
    () => [
      {
        key: 'type_code',
        header: t('efs.masters.package_types.fields.type_code', { ns: 'masters' }),
        cell: (row) => <span className="font-mono text-xs">{row.type_code}</span>,
      },
      {
        key: 'display_name',
        header: t('efs.masters.package_types.fields.display_name', { ns: 'masters' }),
        cell: (row) => row.display_name,
      },
      {
        key: 'container_class',
        header: t('efs.masters.package_types.fields.container_class', { ns: 'masters' }),
        cell: (row) =>
          t(`efs.masters.package_types.container_classes.${row.container_class}`, {
            ns: 'masters',
            defaultValue: row.container_class,
          }),
      },
      {
        key: 'default_tare_kg',
        header: t('efs.masters.package_types.fields.default_tare_kg', { ns: 'masters' }),
        align: 'right',
        cell: (row) => <KgValue value={row.default_tare_kg} />,
      },
      {
        key: 'sort_order',
        header: t('efs.masters.package_types.fields.sort_order', { ns: 'masters' }),
        align: 'center',
        cell: (row) => {
          if (!canManage) return row.sort_order ?? '—';
          const index = rows.findIndex((item) => item.package_type_id === row.package_type_id);
          return (
            <div className="flex justify-center gap-1">
              <span
                className="inline-flex h-8 w-8 cursor-grab items-center justify-center text-efs-text-muted"
                title={t('efs.masters.actions.drag_to_reorder', { ns: 'masters' })}
              >
                <GripVertical className="h-4 w-4" />
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={!canReorderCurrentPage || isReordering || index <= 0}
                aria-label={t('efs.masters.actions.move_up', { ns: 'masters' })}
                title={t('efs.masters.actions.move_up', { ns: 'masters' })}
                onClick={() => movePackageType(row.package_type_id, -1)}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={
                  !canReorderCurrentPage || isReordering || index < 0 || index >= rows.length - 1
                }
                aria-label={t('efs.masters.actions.move_down', { ns: 'masters' })}
                title={t('efs.masters.actions.move_down', { ns: 'masters' })}
                onClick={() => movePackageType(row.package_type_id, 1)}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
      {
        key: 'active',
        header: t('efs.masters.package_types.fields.active', { ns: 'masters' }),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">
              {t('efs.common.status.approved', { defaultValue: '有効' })}
            </Badge>
          ) : (
            <Badge variant="outline">
              {t('efs.common.status.retired', { defaultValue: '無効' })}
            </Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <PackageTypesRowActions row={row} />,
      },
    ],
    [canManage, canReorderCurrentPage, isReordering, movePackageType, rows, t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.package_types.title', { ns: 'masters' })}
        subtitle={t('efs.masters.package_types.subtitle', { ns: 'masters' })}
        count={total}
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('efs.masters.actions.create', { ns: 'masters' })}
            </Button>
          ) : null
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })}
        actions={<MasterExportButton kind="package-types" />}
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.package_type_id}
        rowProps={(row) =>
          canReorderCurrentPage
            ? {
                draggable: !isReordering,
                onDragStart: () => setDraggingId(row.package_type_id),
                onDragOver: (event) => event.preventDefault(),
                onDrop: (event) => {
                  event.preventDefault();
                  dropPackageType(row.package_type_id);
                  setDraggingId(null);
                },
                onDragEnd: () => setDraggingId(null),
                className:
                  draggingId === row.package_type_id ? 'cursor-grabbing opacity-60' : 'cursor-grab',
              }
            : undefined
        }
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: changePerPage,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <PackageTypesFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default PackageTypesPage;
